/**
 * Scrubber inside Core: API helpers and the protocol catalog.
 * Everything is same origin under /api/scrubber on Core's session cookie.
 */
const API_ROOT = process.env.REACT_APP_API_URL || '';
export const API = `${API_ROOT}/api/scrubber`;

export async function getJson(path, opts = {}) {
  const r = await fetch(`${API}${path}`, { credentials: 'include', ...opts });
  let data = null;
  try { data = await r.json(); } catch (_) { data = null; }
  if (!r.ok) {
    const msg = (data && (data.message || data.error)) || `Request failed (${r.status})`;
    const err = new Error(msg); err.status = r.status; err.data = data; throw err;
  }
  return data;
}

export function postJson(path, body, method = 'POST') {
  return getJson(path, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
}

export function postForm(path, formData, method = 'POST') {
  return getJson(path, { method, body: formData });
}

export async function downloadBlob(path, { method = 'GET', body, json, filename }) {
  const opts = { credentials: 'include', method };
  if (json) { opts.headers = { 'Content-Type': 'application/json' }; opts.body = JSON.stringify(json); }
  else if (body) opts.body = body;
  const r = await fetch(`${API}${path}`, opts);
  if (!r.ok) {
    let msg = `Download failed (${r.status})`;
    try { const d = await r.json(); msg = d.message || d.error || msg; } catch (_) { /* keep */ }
    throw new Error(msg);
  }
  const blob = await r.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'download';
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

// ── Who the user is ────────────────────────────────────────────────────────
export function isComplianceUser(user) {
  if (!user) return false;
  const t = user.effective_user_type || user.user_type || null;
  if (t === 'org_superadmin') return false;
  if (t === 'compliance_officer' || t === 'compliance_analyst') return true;
  if (t === 'platform_superuser') return user.support_grant_active === true || user.__supportGrantActive === true;
  if (t) return false;
  const role = String(user.role || '').toLowerCase();
  if (user.app_roles?.scrubber?.role === 'admin') return true;
  return (role === 'admin' && !user.department_id) || role === 'superadmin';
}

export function userEmail(user) { return user?.email || user?.name || 'unknown'; }

// ── The CMS protocols and their universe tables ────────────────────────────
export const PROTOCOLS = [
  { key: 'ODAG',  label: 'ODAG',  title: 'Organization Determinations, Appeals and Grievances', part: 'Part C', needsPeriod: true },
  { key: 'CDAG',  label: 'CDAG',  title: 'Coverage Determinations, Appeals and Grievances', part: 'Part D', needsPeriod: true },
  { key: 'FA',    label: 'FA',    title: 'Formulary and Benefit Administration', part: 'Part D', needsPeriod: true },
  { key: 'SNPCC', label: 'SNPCC', title: 'Special Needs Plans Care Coordination', part: 'SNP', needsPeriod: false },
  { key: 'CPE',   label: 'CPE',   title: 'Compliance Program Effectiveness', part: 'Part C and D', needsPeriod: false },
];

export const TABLES = {
  ODAG: [
    { universeType: 'odag_table1',                  code: 'OD',     label: 'Table 1', name: 'Organization determinations' },
    { universeType: 'odag_table2_reconsiderations', code: 'RECON',  label: 'Table 2', name: 'Reconsiderations' },
    { universeType: 'odag_table3_pymt_c',           code: 'PYMT_C', label: 'Table 3', name: 'Payment determinations and reconsiderations' },
    { universeType: 'odag_table4_eff_c',            code: 'EFF_C',  label: 'Table 4', name: 'Effectuations' },
    { universeType: 'odag_table5_grv_c',            code: 'GRV_C',  label: 'Table 5', name: 'Grievances' },
  ],
  CDAG: [
    { universeType: 'cdag_table1_cd',     code: 'CD',     label: 'Table 1', name: 'Coverage determinations' },
    { universeType: 'cdag_table2_cder',   code: 'CDER',   label: 'Table 2', name: 'Coverage determination exception requests' },
    { universeType: 'cdag_table3_pymt_d', code: 'PYMT_D', label: 'Table 3', name: 'Direct member reimbursement' },
    { universeType: 'cdag_table4_rd',     code: 'RD',     label: 'Table 4', name: 'Redeterminations' },
    { universeType: 'cdag_table5_eff_d',  code: 'EFF_D',  label: 'Table 5', name: 'Effectuations' },
    { universeType: 'cdag_table6_grv_d',  code: 'GRV_D',  label: 'Table 6', name: 'Grievances' },
    { universeType: 'cdag_table7_ar',     code: 'AR',     label: 'Table 7', name: 'At risk determinations' },
  ],
  FA: [
    { universeType: 'fa_table1_rcfa', code: 'RCFA', label: 'Table 1', name: 'Rejected claims, formulary administration' },
    { universeType: 'fa_table2_rct',  code: 'RCT',  label: 'Table 2', name: 'Rejected claims, transition' },
    { universeType: 'fa_table3_pde',  code: 'PDE',  label: 'Table 3', name: 'Prescription drug events' },
    { universeType: 'fa_table4_ne',   code: 'NE',   label: 'Table 4', name: 'Negative enrollment' },
  ],
  SNPCC: [
    { universeType: 'snpcc_snpe', code: 'SNPE',  label: 'Table 1',   name: 'SNP enrollees (SNPE)', endpoint: '/snpcc/validate-snpe', engagement: true },
    { universeType: 'snpcc_ccia', code: 'CC-IA', label: 'Table 1IA', name: 'Care coordination impact analysis', endpoint: '/snpcc/validate-ccia', engagement: false },
  ],
  CPE: [
    { universeType: 'cpe_coa', code: 'COA', label: 'Table 1', name: 'Compliance oversight activities', endpoint: '/validate/cpe-coa', engagement: true },
  ],
};

export function tableLabel(t) { return t ? `${t.label}: ${t.code}` : ''; }

// Universe type to audit slot code, the same map Core enforces on receipt.
export const TYPE_TO_CODE = {
  odag_table1: 'OD', odag_table2_reconsiderations: 'RECON', odag_table3_pymt_c: 'PYMT_C', odag_table4_eff_c: 'EFF_C', odag_table5_grv_c: 'GRV_C',
  cdag_table1_cd: 'CD', cdag_table2_cder: 'CDER', cdag_table3_pymt_d: 'PYMT_D', cdag_table4_rd: 'RD', cdag_table5_eff_d: 'EFF_D', cdag_table6_grv_d: 'GRV_D', cdag_table7_ar: 'AR',
  snpcc_snpe: 'SNPE', snpe: 'SNPE',
};

export const UNIVERSE_OPTIONS = {
  ODAG: TABLES.ODAG.map((t) => ({ value: t.universeType, label: `${t.label} · ${t.name}` })),
  CDAG: TABLES.CDAG.map((t) => ({ value: t.universeType, label: `${t.label} · ${t.name}` })),
  FA: TABLES.FA.map((t) => ({ value: t.universeType, label: `${t.label} · ${t.name}` })),
  SNPCC: [{ value: 'snpe', label: 'Table 1 · SNP enrollees (SNPE)' }],
  CPE: [{ value: 'cpe_coa', label: 'Table 1 · Compliance oversight activities' }],
};

// Dates: the pickers give YYYY-MM-DD; the engine reads CCYY/MM/DD.
export const toCms = (iso) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.replaceAll('-', '/') : iso || '');
export const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
export const fmtNum = (n) => (n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString());
export const pct = (r) => (r === null || r === undefined ? '' : `${Number(r).toFixed(1)}%`);
